import { useCallback, useEffect, useState } from "react";
import { Card, Col, Descriptions, Row, Tag, Typography, message } from "antd";
import { useLocation } from "react-router";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { CGL_AUDIT_REPORT_DETAILS_ENDPOINT } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import { cglFormatDate } from "utils/functions";

const AuditReportView = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { state } = location;

  const auditDate =
    state?.audit_date ?? searchParams.get("audit_date");
  const lineId = state?.line_id ?? searchParams.get("line_id");
  const buyerId = state?.buyer_id ?? searchParams.get("buyer_id");
  const subContractId = state?.sub_contract_id ?? searchParams.get("sub_contract_id");
  const styleId = state?.style_id ?? searchParams.get("style_id");

  const [auditData, setAuditData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(0);

  const getAuditDetails = useCallback(async () => {
    if (!auditDate || !lineId || !styleId || (!buyerId && !subContractId)) {
      message.error("Missing required parameters to load audit details.");
      return;
    }

    setLoading(true);
    setError(null);

    const response = await getData(CGL_AUDIT_REPORT_DETAILS_ENDPOINT, false, {
      audit_date: moment(auditDate).format("YYYY-MM-DD"),
      line_id: lineId,
      style_id: styleId,
      ...(subContractId ? { sub_contract_id: subContractId } : { buyer_id: buyerId }),
    });

    if (response?.data?.code === 200) {
      setAuditData(response?.data?.data || []);
    } else {
      message.error(
        response?.data?.message || "Error getting Audit Report details",
      );
      setError(response?.data?.message || "Could not load this list.");
    }

    setLoading(false);
  }, [auditDate, lineId, buyerId, subContractId, styleId]);

  useEffect(() => {
    getAuditDetails();
  }, [refresh, getAuditDetails]);

  const summaryRecord = auditData?.[0];

  const auditDetailColumns = [
    {
      title: "SL",
      key: "sl",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Defect Categories",
      key: "defect_categories",
      render: (_, record) =>
        record?.defects
          ?.map((item) => item?.defect_category?.name)
          .filter(Boolean)
          .join(", ") || "N/A",
    },
    {
      title: "Operations",
      key: "operations",
      render: (_, record) =>
        record?.operations
          ?.map((item) => item?.operation?.name)
          .filter(Boolean)
          .join(", ") || "N/A",
    },
    {
      title: "Other Defect",
      dataIndex: "other_defect",
      key: "other_defect",
      render: (value) => value || "N/A",
    },
    {
      title: "Defect Qty",
      dataIndex: "defect_qty",
      key: "defect_qty",
      align: "right",
    },
  ];

  return (
    <AppContent
      header={
        <AppPageHeader
          extra={
            <ExtraContent
              hideAddButton={true}
              hideFilterButton={true}
              showBackButton
            />
          }
          title={
            <Typography.Title level={5}>Audit Report Details</Typography.Title>
          }
        />
      }
      content={
        <>
          {summaryRecord && (
            <Card
              bordered
              style={{ margin: "10px 0" }}
              title={<Typography.Title level={5}>Summary</Typography.Title>}
            >
              <Descriptions column={4}>
                <Descriptions.Item label="Audit Date">
                  {cglFormatDate(summaryRecord?.audit_date)}
                </Descriptions.Item>
                <Descriptions.Item label="Line">
                  {summaryRecord?.line_info?.name || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Buyer">
                  {summaryRecord?.buyer?.name || summaryRecord?.sub_contract?.name || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Style">
                  {summaryRecord?.style_info?.style_no || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Total Audits">
                  {auditData?.length || 0}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          <Row gutter={[16, 16]}>
            {auditData?.map((audit, index) => (
              <Col span={24} key={audit?.id || index}>
                <Card
                  bordered
                  loading={loading}
                  title={
                    <Typography.Title level={5}>
                      Audit #{audit?.number_of_audit ?? index + 1}
                    </Typography.Title>
                  }
                >
                  <Descriptions column={4} style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="Auditor">
                      {audit?.auditor?.name || "N/A"}
                    </Descriptions.Item>
                    <Descriptions.Item label="AQL">
                      {audit?.aql || "N/A"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Lot Qty">
                      {audit?.lot_qty ?? "N/A"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Sample Qty">
                      {audit?.sample_qty ?? "N/A"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Number of Audit">
                      {audit?.number_of_audit || "N/A"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Pass/Fail">
                      {audit?.pass_fail ? (
                        <Tag color={audit.pass_fail === "Pass" ? "green" : "red"}>
                          {audit.pass_fail}
                        </Tag>
                      ) : (
                        "N/A"
                      )}
                    </Descriptions.Item>
                  </Descriptions>

                  <ListTable
                    bordered
                    size="small"
                    loading={loading}
                    error={error}
                    onRetry={() => getAuditDetails()}
                    emptyText="No records to show yet."
                    pagination={false}
                    dataSource={audit?.audit_details || []}
                    columns={auditDetailColumns}
                    rowKey={(record) => record?.id}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </>
      }
    />
  );
};

export default AuditReportView;
